import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Loader, Message } from 'rsuite';
import DatePicker from '../shared/DatePicker';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import { toDateOnlyString } from '../../utils/payPeriods';
import PayrollCopyableFigure from './PayrollCopyableFigure';

// The last look at a run before it is signed off.
//
// Completing is the one irreversible thing on this page, so the dialog is a
// review rather than a confirmation: the figures somebody is about to declare
// final are in front of them, not one click away behind an "are you sure".
//
// It is also where the workbook gets downloaded. A payroll has to be
// produceable for years, and the only copy that survives this application is
// the Excel export - so completing is gated on one having been taken. That is
// a rule on the server too (see the complete route); the disabled button here
// is the explanation, not the enforcement.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
};

export default function PayrollCompleteRunModal({
  open,
  result,
  period,
  drift = null,
  journalEntryCreated = false,
  timesheetDrift = null,
  timesheetDriftLoading = false,
  timesheetDriftFailed = false,
  onRepull,
  repulling = false,
  exports = [],
  hasCurrentExport = false,
  figuresUpdatedAt = null,
  exportsLoading = false,
  onExport,
  exporting = false,
  sharePointUrl = null,
  sharePointConfirmed = false,
  onSharePoint,
  onClose,
  onComplete,
  completing = false,
}) {
  const [paymentDate, setPaymentDate] = useState(null);
  const [journalEntry, setJournalEntry] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // Cleared on each open. A payment date left over from a dialog somebody
  // backed out of is the last thing that should be pre-filled on the next run.
  //
  // The journal entry box is seeded from the run rather than cleared: it is a
  // fact already recorded about this run, and re-opening the dialog must not
  // present it as un-done.
  useEffect(() => {
    if (!open) return;
    setPaymentDate(null);
    setJournalEntry(Boolean(journalEntryCreated));
    setError(null);
    setDownloading(false);
  }, [open, journalEntryCreated]);

  const totals = result?.totals || {};
  const employees = result?.employees || [];

  const cashNeeded = (Number(totals.totalCompensation) || 0) + (Number(totals.reimbursements) || 0);
  const empowerTotal = [
    totals.retirement?.traditional,
    totals.retirement?.roth,
    totals.retirement?.employerMatch,
    totals.retirement?.adjustment,
  ].filter((value) => value != null).reduce((sum, value) => sum + Number(value), 0);

  const missingRate = employees.filter((employee) => employee.totalCompensation == null).length;
  const pendingExpenses = totals.reimbursementPending || 0;

  // The trail is a list, newest first, so the most recent download is the head
  // of it. Any entry at all satisfies the server; the latest is what gets named
  // in the SharePoint instruction, because that is the file sitting in the
  // downloads folder.
  const latestExport = exports[0] || null;

  // Whether a current export exists is the server's answer, taken as given.
  // This used to be recomputed here from the re-pull timestamp, which made it a
  // second definition of "current" - and a narrower one: a correction, a
  // retirement adjustment or a reimbursement refresh rebuilds the run's figures
  // without touching repulled_at, so a stale workbook still read as Downloaded
  // and the button stayed enabled until the server refused it. The completion
  // guard and this dialog now read the same verdict.
  // TimeCamp checked against this run, asked once when the dialog opened.
  // Unavailable is not the same as clean: a check that could not be made is
  // said so rather than shown as "nothing changed".
  const driftChecking = timesheetDriftLoading;
  const driftUnavailable = timesheetDriftFailed || Boolean(timesheetDrift?.unavailable);
  const driftRows = timesheetDrift?.employees || [];
  const hasDrift = Boolean(timesheetDrift?.changed) && driftRows.length > 0;

  const hasExport = Boolean(hasCurrentExport);
  const staleExport = !hasExport && exports.length > 0;
  const busy = downloading || exporting;

  const handleExport = async () => {
    setDownloading(true);
    setError(null);
    try {
      await onExport?.();
    } finally {
      setDownloading(false);
    }
  };

  const handleComplete = () => {
    // Checked before the payment date because it is the harder one to fix: a
    // date is typed here, a missing export means going back for the file.
    if (!hasExport) {
      setError('Download the Excel export first. It is the copy kept for tax and audit.');
      return;
    }
    if (!paymentDate) {
      setError('Enter the date this payroll will be paid.');
      return;
    }
    onComplete(toDateOnlyString(paymentDate), journalEntry);
  };

  // `copyValue` marks the figures that get carried somewhere else - into the
  // bank, into Empower - and makes those click-to-copy. The rest of the summary
  // is read here and nowhere else, so it stays plain text: a dialog where every
  // number is a button reads as a form rather than a statement.
  const line = (label, value, { strong = false, copyValue = null } = {}) => (
    <div className={`payroll-complete__line${strong ? ' payroll-complete__line--strong' : ''}`}>
      <span>{label}</span>
      <span className="bdc-num">
        {copyValue == null ? value : (
          <PayrollCopyableFigure value={copyValue} label={label}>
            {value}
          </PayrollCopyableFigure>
        )}
      </span>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} size="sm" overflow>
      <Modal.Header>
        <Modal.Title>Review and complete</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* First thing in the dialog, ahead of the figures themselves. A
            warning that the numbers below are out of date is worth nothing
            underneath them - by then they have been read. */}
        {driftChecking ? (
          <Message showIcon type="info" style={{ marginBottom: 14 }}>
            <Loader size="xs" style={{ marginRight: 8 }} />
            Checking TimeCamp for hours logged since this run was pulled...
          </Message>
        ) : null}

        {!driftChecking && driftUnavailable ? (
          <Message showIcon type="warning" style={{ marginBottom: 14 }}>
            TimeCamp could not be reached, so this run has not been checked for
            newer hours. You can still complete it - the figures below are what
            will be filed.
          </Message>
        ) : null}

        {!driftChecking && hasDrift ? (
          <Message showIcon type="warning" style={{ marginBottom: 14 }}>
            <strong>
              TimeCamp has different hours for this period than this run was pulled with.
            </strong>
            <ul className="payroll-complete__drift">
              {driftRows.slice(0, 6).map((row) => (
                <li key={row.employeeId}>
                  {row.name}
                  {row.added ? ' (not on this run)' : ''}
                  {row.removed ? ' (no longer has hours)' : ''}
                  {': '}
                  <span className="bdc-num">
                    {fmtHours(row.before)} to {fmtHours(row.after)} h
                    {' '}({row.delta > 0 ? '+' : ''}{fmtHours(row.delta)})
                  </span>
                </li>
              ))}
              {driftRows.length > 6 ? (
                <li className="text-muted">and {driftRows.length - 6} more</li>
              ) : null}
            </ul>
            Completing now files the figures below, as they stand. Re-pull to take
            the newer hours instead - this closes the review so the updated figures
            can be checked, and the export will need downloading again.
            <div className="payroll-complete__drift-action">
              <Button
                appearance="primary"
                color="orange"
                size="sm"
                onClick={onRepull}
                loading={repulling}
                disabled={!onRepull || completing}
              >
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                {' '}Re-pull hours now
              </Button>
            </div>
          </Message>
        ) : null}

        <p className="text-muted" style={{ fontSize: 13, marginTop: 0 }}>
          {period ? <strong>{period}</strong> : null}
          {period ? ' · ' : null}
          {employees.length} employee{employees.length === 1 ? '' : 's'}
        </p>

        <div className="payroll-complete__summary">
          {line('Total hours', fmtHours(totals.total))}
          {line('Paid hours', fmtHours(totals.compensatedHours))}
          {line('Compensation total', fmtMoney(totals.totalCompensation))}
          {line('Reimbursements total', fmtMoney(totals.reimbursements))}
          {line('Cash needed', fmtMoney(cashNeeded), { strong: true, copyValue: cashNeeded })}
          {line('Total uploaded to Empower', fmtMoney(empowerTotal), { strong: true, copyValue: empowerTotal })}
        </div>

        {/* Anything that would make somebody want to stop, said before the
            button rather than discovered after it. Warnings, not blocks: a run
            can legitimately be completed with a pending expense on it, and it
            is not this dialog's place to decide otherwise. */}
        {missingRate > 0 ? (
          <Message type="warning" showIcon style={{ marginTop: 14 }}>
            {missingRate} employee{missingRate === 1 ? ' has' : 's have'} no pay rate on file, so
            {missingRate === 1 ? ' their' : ' their'} compensation is not included in these totals.
          </Message>
        ) : null}

        {drift?.changed ? (
          <Message type="warning" showIcon style={{ marginTop: 14 }}>
            Expenses have changed since this run was filed and have not been synced.
            Completing now files it with the reimbursement figures shown above.
          </Message>
        ) : null}

        {pendingExpenses > 0 ? (
          <Message type="info" showIcon style={{ marginTop: 14 }}>
            {fmtMoney(pendingExpenses)} of expenses are still awaiting approval. They are
            not part of the reimbursement total and will not be paid on this run.
          </Message>
        ) : null}

        {/* The record-keeping step. Above the payment date because it has to
            happen before this dialog can do anything, and because burying a
            required action under the optional ones is how it gets missed. */}
        <div className="payroll-complete__export">
          <div className="payroll-complete__export-head">
            <span className="payroll-complete__export-title">
              Excel export for tax and audit
            </span>
            {hasExport ? (
              <span className="payroll-complete__export-badge payroll-complete__export-badge--done">
                <i className="bi bi-check-circle-fill" aria-hidden="true" /> Downloaded
              </span>
            ) : (
              <span className="payroll-complete__export-badge">Required</span>
            )}
          </div>

          <p className="payroll-complete__export-copy">
            This workbook is the copy of the payroll that survives outside this
            system. A run cannot be completed until it has been downloaded.
          </p>

          {/* The one case where an export exists and still does not count. Said
              plainly, because "Required" next to a file somebody knows they
              already downloaded reads as a bug. */}
          {staleExport ? (
            <p className="payroll-complete__export-stale">
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
              {' '}The figures on this run changed
              {figuresUpdatedAt ? ` on ${new Date(figuresUpdatedAt).toLocaleString()}` : ''}
              , after the last download - a re-pull, a correction, a loan
              repayment or a reimbursement refresh. That copy no longer matches
              this run. Download it again.
            </p>
          ) : null}

          <Button
            appearance={hasExport ? 'default' : 'primary'}
            onClick={handleExport}
            loading={busy}
            disabled={completing}
          >
            <i className="bi bi-file-earmark-excel" aria-hidden="true" />
            {hasExport ? ' Download again' : ' Download Excel export'}
          </Button>

          {exportsLoading && !hasExport ? (
            <div className="text-muted small mt-2">Checking for an earlier download...</div>
          ) : null}

          {/* Named, dated and attributed: the point of the trail is that
              somebody can later ask which file this was and who took it. */}
          {latestExport ? (
            <div className="payroll-complete__export-trail">
              <code>{latestExport.filename}</code>
              <span>
                {new Date(latestExport.downloaded_at).toLocaleString()}
                {latestExport.downloaded_by_name ? ` by ${latestExport.downloaded_by_name}` : ''}
              </span>
              {exports.length > 1 ? (
                <span className="text-muted">
                  {exports.length} downloads recorded for this run
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Shown only once there is a file to upload - an instruction to go
            and put something on SharePoint is noise until it exists. The tick
            box is not a condition of completing: the upload usually happens
            after the sign-off, and a box that cannot honestly be ticked yet is
            one people tick to get past. It stays available on the completed
            run, like the journal entry box below. */}
        {hasExport ? (
          <Message type="info" showIcon style={{ marginTop: 14 }}>
            <strong>Upload this file to SharePoint.</strong> Keeping a second copy
            outside this system is what makes the record redundant - if this
            application is ever unavailable, SharePoint is where the payroll is
            produced from.
            {/* Linked when the site is configured, named in words when it is
                not - an instruction to upload somewhere is easier to follow
                with the somewhere attached. */}
            {sharePointUrl ? (
              <div className="payroll-complete__sharepoint-link">
                <a href={sharePointUrl} target="_blank" rel="noopener noreferrer">
                  Open SharePoint <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                </a>
              </div>
            ) : null}
            <div className="payroll-complete__sharepoint">
              <Checkbox
                checked={sharePointConfirmed}
                onChange={(_value, checked) => onSharePoint?.(checked)}
                disabled={!onSharePoint}
              >
                Uploaded to SharePoint
              </Checkbox>
              <div className="text-muted small">
                Optional here. This can be ticked later, on the completed run.
              </div>
            </div>
          </Message>
        ) : null}

        <div style={{ marginTop: 18 }}>
          <label className="form-label small fw-semibold" htmlFor="payroll-payment-date">
            Payment date
          </label>
          <DatePicker
            id="payroll-payment-date"
            value={paymentDate}
            onChange={(value) => { setPaymentDate(value); setError(null); }}
            format="MM/dd/yyyy"
            oneTap
            placement="topStart"
            preventOverflow
            style={{ width: '100%' }}
          />
          <div className="text-muted small mt-1">
            The day this payroll is paid. Not necessarily today.
          </div>
          {error ? <div className="text-danger small mt-1">{error}</div> : null}
        </div>

        {/* Asked here because this is where somebody is looking at the run one
            last time, but never a condition of completing: the entry is usually
            keyed into QuickBooks after the sign-off, and it stays tickable on
            the completed run. Left unticked, the run simply shows as still
            owing one. */}
        <div className="payroll-complete__journal">
          <Checkbox
            checked={journalEntry}
            onChange={(_value, checked) => setJournalEntry(checked)}
          >
            Journal entry created in QuickBooks
          </Checkbox>
          <div className="text-muted small">
            Optional. This can be ticked later, on the completed run.
          </div>
        </div>

        <Message type="warning" showIcon style={{ marginTop: 16 }}>
          Completing locks this run. Corrections, loan repayments,
          reimbursement syncing and its name and notes can no longer be changed.
          The journal entry box above stays editable.
        </Message>
      </Modal.Body>

      <Modal.Footer>
        <NsiButton onClick={onClose} disabled={completing}>
          Cancel
        </NsiButton>
        {/* Disabled rather than hidden, with the reason beside it: a button
            that vanishes leaves somebody looking for what they did wrong. The
            server refuses this too - see the complete route. */}
        <NsiButton
          variant="primary"
          onClick={handleComplete}
          loading={completing}
          disabled={!hasExport || busy}
        >
          Complete payroll
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}
